import React, { useEffect, useState } from 'react';
import { Dropdown, Modal, Button } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSidebar } from '../../contexts/SidebarContext.tsx';
import { useLoading } from '../../contexts/LoadingContext.tsx';
import { AuthHelper } from '../../utils/authHelper';
import LogoutModal from '../Auth/LogoutModal.tsx';
// import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined';
// import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import { HiMenuAlt2 } from 'react-icons/hi';
import axios from 'axios';
import apiConfig from '../pages/apiConfig.ts';
import { API_ENDPOINTS } from '../utils.ts';
import { menuItems } from '../../../src/menu.ts';
import NotificationDropdown from './notifications/NotificationDropdown';
import { useNotifications } from './notifications/useNotifications';
import './Header.scss';

interface HeaderProps {
  customTitle?: string;
  customerData?: {
    sixDigitId: string;
    customerName: string;
  };
}

const Header: React.FC<HeaderProps> = ({ customTitle, customerData }) => {
  const { toggleSidebar, toggleCollapsed } = useSidebar();
  const { showLoader, hideLoader } = useLoading();
  const [fullName, setFullName] = useState<string>('');
  const [profileImage, setProfileImage] = useState<string>('');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [pageTitle, setPageTitle] = useState<string>('Dashboard');
  const [billingCustomerData, setBillingCustomerData] = useState<{
    sixDigitId: string;
    customerName: string;
  } | null>(null);
  const [orderData, setOrderData] = useState<{
    orderId: string;
    type: string;
  } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize notifications hook
  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications
  } = useNotifications();

  // Extract sixDigitId from billing details route
  const getBillingDetailsSixDigitId = (pathname: string): string | null => {
    const billingDetailsMatch = pathname.match(/\/billing-details\/([^\/]+)/);
    return billingDetailsMatch ? billingDetailsMatch[1] : null;
  };

  // Fetch customer data when on billing details page
  useEffect(() => {
    const fetchCustomerData = async (sixDigitId: string) => {
      try {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) return;

        const response = await axios.get(
          `${apiConfig.apiBaseURL}${API_ENDPOINTS.GET_SINGLE_USER}/${sixDigitId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (response.data.status === "Success") {
          setBillingCustomerData({
            sixDigitId: response.data.data.sixDigitId || sixDigitId, // Use sixDigitId instead of userId
            customerName: response.data.data.customerName || 'Unknown Customer'
          });
        }
      } catch (error) {
        console.error('Error fetching customer data for header:', error);
        setBillingCustomerData({
          sixDigitId: sixDigitId,
          customerName: 'Unknown Customer'
        });
      }
    };

    const sixDigitId = getBillingDetailsSixDigitId(location.pathname);
    
    if (sixDigitId) {
      fetchCustomerData(sixDigitId);
    } else {
      setBillingCustomerData(null);
    }
  }, [location.pathname]);

  // Fetch order data for detail pages
  useEffect(() => {
    const fetchOrderData = async (orderId: string, type: string) => {
      try {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) return;

        const response = await axios.get(
          `${apiConfig.apiBaseURL}/order/get-single-order/${orderId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (response.data.status === "Success" && response.data.data) {
          console.log('Header: Fetched order data:', response.data.data.orderId);
          setOrderData({
            orderId: response.data.data.orderId,
            type: type
          });
        }
      } catch (error) {
        console.error('Error fetching order data for header:', error);
      }
    };

    let orderId: string | null = null;
    let type: string = '';

    if (location.pathname.includes('/order-details/')) {
      orderId = location.pathname.split('/order-details/')[1];
      type = 'order';
    } else if (location.pathname.includes('/posthumous-details/')) {
      orderId = location.pathname.split('/posthumous-details/')[1];
      type = 'posthumous';
    } else if (location.pathname.includes('/milestone-details/')) {
      orderId = location.pathname.split('/milestone-details/')[1];
      type = 'milestone';
    }

    if (orderId && type) {
      fetchOrderData(orderId, type);
    } else {
      setOrderData(null);
    }
  }, [location.pathname]);

  // Function to get page title based on current route
  const getPageTitle = (pathname: string): string => {
    // If custom title is provided, use it
    if (customTitle) {
      return customTitle;
    }

    // Check for billing details route and use fetched customer data
    if (billingCustomerData && pathname.includes('/billing-details/')) {
      return `#${billingCustomerData.sixDigitId} - ${billingCustomerData.customerName}`;
    }

    // If we have customer data prop for billing details, format it
    if (customerData && pathname.includes('/billing-details/')) {
      return `#${customerData.sixDigitId} - ${customerData.customerName}`;
    }

    // Handle detail pages with actual order IDs from API data
    if (pathname.includes('/order-details/')) {
      return orderData ? `Order ID: #${orderData.orderId}` : 'Orders';
    }

    if (pathname.includes('/posthumous-details/')) {
      return orderData ? `Posthumous Order ID: #${orderData.orderId}` : 'Posthumous Gifts';
    }

    if (pathname.includes('/milestone-details/')) {
      // Show actual milestone order ID if available
      if (orderData && orderData.orderId) {
        return `Order ID: ${orderData.orderId}`;
      } else {
        return 'Milestone Details';
      }
    }

    // Handle discount code details page
    if (pathname.includes('/discount-codes-details/')) {
      return 'Discount Code Details';
    }

    // Handle subscription details page
    if (pathname.includes('/subscriptions/')) {
      const subscriptionId = pathname.split('/subscriptions/')[1];
      return subscriptionId ? `Subscription Details` : 'Subscription Plans';
    }

    // Handle profile page
    if (pathname === '/profile' || pathname === '/profile/') {
      return 'Profile';
    }

    // Remove query parameters and hash
    const cleanPath = pathname.split('?')[0].split('#')[0];
    
    const menuItem = menuItems.find(item => {
      if (item.path === cleanPath) return true;
      if (item.path === cleanPath + '/' || item.path + '/' === cleanPath) return true;
      if (cleanPath.startsWith(item.path + '/') && item.path !== '/') return true;
      
      return false;
    });

    return menuItem ? menuItem.title : 'Dashboard';
  };

  // Update page title when route changes
  useEffect(() => {
    const title = getPageTitle(location.pathname);
    setPageTitle(title);
  }, [location.pathname, customTitle, customerData, billingCustomerData, orderData]);

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      try {
        const response = await axios.get(
          `${apiConfig.apiBaseURL}${API_ENDPOINTS.GET_PROFILE}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data?.status === 'Success' && response.data?.data) {
          const { firstName, lastName, profileUrl } = response.data.data;
          setFullName(`${firstName} ${lastName}`);
          setProfileImage(profileUrl || '');
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    fetchUser();

    // Listen for profile updates
    const handleProfileUpdate = () => {
      fetchUser();
    };

    window.addEventListener('profileUpdated', handleProfileUpdate);

    // Cleanup
    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, []);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const handleLogoutClick = (e: any) => {
    e.preventDefault();
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = (clearRememberMe: boolean = false) => {
    showLoader('Processing your request...');
    
    // Clear auth data
    AuthHelper.clearAuthData();
    
    // Clear remember me data if requested
    if (clearRememberMe) {
      AuthHelper.clearRememberMeData();
    }
    
    setShowLogoutModal(false);
    hideLoader();
    navigate('/');
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  // Demo function to test loader
  const testLoader = () => {
    showLoader('Processing your request...');
    setTimeout(() => {
      hideLoader();
    }, 3000);
  };

  return (
    <section className='header'>
      <div className='d-flex justify-content-between' style={{ alignItems: "center" }}>
        <aside className='main_title d-flex align-items-center'>
          {/* Mobile sidebar toggle button */}
          <Button
            variant="link"
            className="d-lg-none p-0 me-3 mobile-toggle"
            onClick={toggleSidebar}
            style={{ border: 'none', background: 'transparent' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </Button>

          {/* Desktop sidebar collapse toggle button */}
          <Button
            variant="link"
            className="d-none d-lg-block p-0 me-3 desktop-toggle"
            onClick={toggleCollapsed}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#3957A0',
              fontSize: '24px',
              lineHeight: '1'
            }}
            title="Toggle sidebar"
          >
            <HiMenuAlt2 />
          </Button>

          <h2>{pageTitle}</h2>
        </aside>
        <aside className='profile'>
          <div className="d-inline-block align-items-center">
            
            
            {/* Notification Dropdown */}
            <NotificationDropdown
              notifications={notifications}
              unreadCount={unreadCount}
              loading={notificationsLoading}
              onMarkAsRead={markAsRead}
              onMarkAllAsRead={markAllAsRead}
              onRefresh={refreshNotifications}
              onDeleteNotification={deleteNotification}
            />

            {/* Profile Dropdown */}
            <Dropdown className="d-inline-block profile-drop">
              <Dropdown.Toggle variant="light" className="d-flex align-items-center border-0 bg-transparent text-white">
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt="Profile"
                    className="rounded-circle"
                    style={{
                      width: 40,
                      height: 40,
                      objectFit: 'cover',
                      marginRight: '8px',
                      border: "solid 1px #ddd",
                    }}
                  />
                ) : (
                  <div
                    className="rounded-circle bg-white text-dark d-flex justify-content-center align-items-center"
                    style={{
                      width: 40,
                      height: 40,
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      marginRight: '8px',
                      border:"solid 1px #ddd",
                    }}
                  >
                    {getInitials(fullName)}
                  </div>
                )}
                <aside>
                  <span className="text-white fw-bold">{fullName}</span>
                </aside>
                <ArrowDropDownOutlinedIcon />
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                <Dropdown.Item onClick={() => navigate('/profile')}>
                  <PersonOutlinedIcon /><span>Profile</span>
                </Dropdown.Item>
                <Dropdown.Item onClick={handleLogoutClick}>
                  <LogoutOutlinedIcon /><span>Logout</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </aside>
      </div>

      {/* Enhanced Logout Modal */}
      <LogoutModal
        show={showLogoutModal}
        onHide={handleLogoutCancel}
        onConfirmLogout={handleLogoutConfirm}
      />
    </section>
  );
};

export default Header;
